// The effective analytics consent detector (products/analytics.ts), kept out of that module so it is
// unit-testable under plain Node (analytics.ts value-imports the analytics SDK).
//
// Precedence, re-evaluated on every call (the SDK asks before each send, and the page may set
// `window.RaventicConsent` later in the visit):
//   1. `window.RaventicConsent` — the shop's / CMP's explicit signal ALWAYS wins when it is set:
//      a boolean is the answer; a function is called with "analytics_storage" (sync or async,
//      `this` = the window, as the SDK's manual-path resolver calls it).
//   2. the customer config's site-specific detector (`analytics.consent`, e.g. `cookieScript(…)`
//      from `@raventic/sdk/consent`), when there is one;
//   3. otherwise granted (the SDK's historical default for shops with no consent wiring).
// A `RaventicConsent` of any other type (null, a string, …) is not a usable signal and counts as
// unset. Answers are normalised to a strict boolean, failing closed: only `true` (or a Promise of
// `true`) grants — "granted", 1, an object… are denied (the wrapper would otherwise take any truthy
// value as consent). Throws and rejections — from the shop's function or the detector — are NOT
// caught here (a rejected RaventicConsent does not fall through to the detector): they propagate to
// ConsentAwareIdentifierWrapper, which logs them and treats them as denied.
//
// Type-only imports, so Node's type stripping loads it (`pnpm test`).

import type { ConsentDetection } from "../config.ts";

// `unknown` on purpose: the global is page-controlled, so its type is checked at call time.
export interface RaventicConsentWindow {
  RaventicConsent?: unknown;
}

const strictly = (answer: unknown): boolean | Promise<boolean> =>
  answer instanceof Promise ? answer.then((v: unknown) => v === true) : answer === true;

export const withRaventicConsent =
  (win: RaventicConsentWindow, site?: ConsentDetection): ConsentDetection =>
  () => {
    const signal = win.RaventicConsent;
    if (typeof signal === "boolean") return signal;
    if (typeof signal === "function") return strictly((signal as (type: string) => unknown).call(win, "analytics_storage"));
    return site ? strictly(site()) : true;
  };
